import {
  ArrowForward,
  Edit,
  InfoOutlined,
  KeyboardReturn,
} from "@mui/icons-material";
import { Box, ClickAwayListener, Collapse, Fade, FormControlLabel, IconButton, InputAdornment, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { colors, alphaVar } from "@components/ui/colors";

interface ServiceAliasFieldProps {
  enabled: boolean;
  value: string;
  originalServiceName?: string;

  onEnabledChange: (enabled: boolean) => void;
  onValueChange: (value: string) => void;

  error?: boolean;
  helperText?: string;

  disabled?: boolean;
  size?: "small" | "medium";

  description?: string;
  fieldLabel?: string;
  placeholder?: string;
}

export const ServiceAliasField = ({
  enabled,
  value,
  originalServiceName = "",
  onEnabledChange,
  onValueChange,
  error = false,
  helperText,
  disabled = false,
  size = "medium",
  description = "This alias will be displayed on the scope of work and invoice.",
  fieldLabel = "Custom Service Alias",
  placeholder,
}: ServiceAliasFieldProps) => {
  const trimmedOriginalName =
    originalServiceName.trim() || "Original service";

  const trimmedAlias = value.trim();

  const [isEditingAlias, setIsEditingAlias] = useState(
    enabled && !trimmedAlias,
  );

  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsEditingAlias(false);
      setShowInfo(false);
      return;
    }

    if (!value.trim()) {
      setIsEditingAlias(true);
    }
  }, [enabled, value]);

  const finishEditing = () => {
    if (!value.trim()) return;

    setIsEditingAlias(false);
  };

  return (
    <Box
      sx={{
        p: 2,
        mt:1,
        borderRadius: "12px",
        border: `1px solid ${colors.gray[200]}`,
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
      }}
    >
      <FormControlLabel
        disabled={disabled}
        control={
          <Switch
            size={size}
            checked={enabled}
            onChange={(event) => {
              const nextEnabled = event.target.checked;

              onEnabledChange(nextEnabled);

              if (nextEnabled) {
                setIsEditingAlias(true);
              } else {
                onValueChange("");
                setIsEditingAlias(false);
                setShowInfo(false);
              }
            }}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: colors.orange.base,
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                {
                  backgroundColor: colors.orange.base,
                },
            }}
          />
        }
        label={
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: colors.gray[800],
              }}
            >
              Use custom service alias
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: colors.gray[500],
                display: "block",
              }}
            >
              {description}
            </Typography>
          </Box>
        }
        sx={{
          m: 0,
          alignItems: "center",
        }}
      />

      <Collapse in={enabled}>
        <Box sx={{ mt: 1.5 }}>
          {isEditingAlias ? (
            <ClickAwayListener onClickAway={finishEditing}>
              <Box px={1} >
                <TextField
                  autoFocus
                  fullWidth
                  size={size}
                  disabled={disabled}
                  label={fieldLabel}
                  placeholder={
                    placeholder ||
                    originalServiceName ||
                    "Enter a custom service alias"
                  }
                  value={value}
                  error={error}
                  helperText={helperText}
                  onChange={(event) => {
                    onValueChange(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      event.stopPropagation();

                      finishEditing();
                    }

                    if (
                      event.key === "Escape" &&
                      value.trim()
                    ) {
                      event.preventDefault();
                      event.stopPropagation();

                      setIsEditingAlias(false);
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Apply alias (Enter)">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!value.trim()}
                              onMouseDown={(event) => {
                                event.preventDefault();
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                                finishEditing();
                              }}
                              sx={{
                                width: 30,
                                height: 30,
                                color: value.trim()
                                  ? colors.orange.base
                                  : colors.gray[300],
                                bgcolor: value.trim()
                                  ? alphaVar(colors.orange.base,
                                      0.08,
                                    )
                                  : "transparent",
                                "&:hover": {
                                  bgcolor: alphaVar(colors.orange.base,
                                    0.14,
                                  ),
                                },
                              }}
                            >
                              <KeyboardReturn
                                sx={{ fontSize: 18 }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    maxLength: 150,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      fontSize: 14,

                      "& fieldset": {
                        borderColor: error
                          ? colors.danger[500]
                          : alphaVar(colors.orange.base,
                              0.2,
                            ),
                      },

                      "&:hover fieldset": {
                        borderColor: error
                          ? colors.danger[500]
                          : colors.orange[400],
                      },

                      "&.Mui-focused fieldset": {
                        borderColor: error
                          ? colors.danger[500]
                          : colors.orange.base,
                        borderWidth: 1.5,
                      },
                    },
                  }}
                />
              </Box>
            </ClickAwayListener>
          ) : (
            <Box
              sx={{
                p: 1.25,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderRadius: 2,
                bgcolor: alphaVar(colors.orange.base,
                  0.055,
                ),
                border: `1px solid ${alphaVar(colors.orange.base,
                  0.14,
                )}`,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.gray[600],
                  overflowWrap: "anywhere",
                }}
              >
                {trimmedOriginalName}
              </Typography>

              <ArrowForward
                sx={{
                  flexShrink: 0,
                  fontSize: 18,
                  color: colors.orange.base,
                }}
              />

              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  fontWeight: 800,
                  color: colors.orange[700],
                  overflowWrap: "anywhere",
                  textAlign: "right",
                }}
              >
                {trimmedAlias}
              </Typography>

              {!disabled ? (
                <Tooltip title="Edit service alias">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setIsEditingAlias(true);
                    }}
                    sx={{
                      flexShrink: 0,
                      width: 30,
                      height: 30,
                      color: colors.orange.base,
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      border: `1px solid ${alphaVar(colors.orange.base,
                        0.18,
                      )}`,
                      "&:hover": {
                        bgcolor: alphaVar(colors.orange.base,
                          0.08,
                        ),
                      },
                    }}
                  >
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          )}

        <Box
  sx={{
    mt: 0.75,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 0.75,
    minWidth: 0,
  }}
>
 {showInfo ? (
  <Fade in={showInfo} timeout={200}>
    <Typography
      sx={{
        fontSize: 12,
        lineHeight: 1.4,
        color: "#2563EB",
        whiteSpace: "normal",
      }}
    >
      The original service name is preserved. The alias is only used
      as its display name.
    </Typography>
  </Fade>
) : null}

  <Tooltip
    title={showInfo ? "Hide information" : "Alias information"}
  >
    <IconButton
      size="small"
      onClick={() => {
        setShowInfo((current) => !current);
      }}
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        color: "#2563EB",
        bgcolor: "var(--fx-colors-info-100, #DBEAFE)",
        border: "1px solid #93C5FD",
        flexShrink: 0,
        "&:hover": {
          bgcolor: "var(--fx-colors-info-100, #DBEAFE)",
        },
      }}
    >
      <InfoOutlined sx={{ fontSize: 17 }} />
    </IconButton>
  </Tooltip>
</Box>
        </Box>
      </Collapse>
    </Box>
  );
};